import React, { useState, useEffect } from 'react';
import { motion } from 'framer-motion';
import { MessageSquare, Send, User, Bot, Clock, Phone, Search, Power, PowerOff } from 'lucide-react';
import { useAuth } from '../../context/AuthContext';
import axios from 'axios';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

const Conversations = () => {
  const { token } = useAuth();
  const [conversations, setConversations] = useState([]);
  const [selectedConv, setSelectedConv] = useState(null);
  const [loading, setLoading] = useState(true);
  const [searchTerm, setSearchTerm] = useState('');
  const [messageInput, setMessageInput] = useState('');

  useEffect(() => {
    fetchConversations();
  }, []);

  const fetchConversations = async () => {
    try {
      const response = await axios.get(`${API}/admin/conversations`, {
        headers: { 'Authorization': `Bearer ${token}` }
      });
      setConversations(response.data);
      if (response.data.length > 0) {
        setSelectedConv(response.data[0]);
      }
    } catch (error) {
      console.error('Error fetching conversations:', error);
    } finally {
      setLoading(false);
    }
  };

  const toggleMode = async (convId, currentMode) => {
    const newMode = currentMode === 'automatic' ? 'manual' : 'automatic';
    try {
      await axios.patch(`${API}/admin/conversations/${convId}/mode?mode=${newMode}`, {}, {
        headers: { 'Authorization': `Bearer ${token}` }
      });
      fetchConversations();
    } catch (error) {
      console.error('Error toggling mode:', error);
    }
  };

  const sendMessage = async () => {
    if (!messageInput.trim() || !selectedConv) return;

    try {
      await axios.post(
        `${API}/admin/conversations/${selectedConv.id}/messages?message_content=${encodeURIComponent(messageInput)}`,
        {},
        { headers: { 'Authorization': `Bearer ${token}` } }
      );
      setMessageInput('');
      fetchConversations();
    } catch (error) {
      console.error('Error sending message:', error);
    }
  };

  const filteredConversations = conversations.filter(conv =>
    conv.customer_name.toLowerCase().includes(searchTerm.toLowerCase()) ||
    conv.customer_phone.includes(searchTerm)
  );

  if (loading) {
    return (
      <div className="flex items-center justify-center h-96">
        <div className="w-12 h-12 border-4 rounded-full animate-spin" 
             style={{borderColor: '#D4AF37', borderTopColor: 'transparent'}}></div>
      </div>
    );
  }

  return (
    <div>
      {/* Header */}
      <div className="mb-6">
        <h1 className="text-3xl font-bold text-gray-800 mb-2">Conversaciones</h1>
        <p className="text-gray-600">Monitorea y gestiona las conversaciones con clientes</p>
      </div>

      {/* Stats */}
      <div className="grid grid-cols-1 md:grid-cols-4 gap-4 mb-6">
        <div className="bg-white p-4 rounded-xl shadow-sm border border-gray-200">
          <p className="text-sm text-gray-600">Total</p>
          <p className="text-2xl font-bold text-gray-800">{conversations.length}</p>
        </div>
        <div className="bg-white p-4 rounded-xl shadow-sm border border-gray-200">
          <p className="text-sm text-gray-600">Activas Hoy</p>
          <p className="text-2xl font-bold text-green-600">
            {conversations.filter(c => {
              const lastMsg = new Date(c.last_message_at);
              const today = new Date();
              return lastMsg.toDateString() === today.toDateString();
            }).length}
          </p>
        </div>
        <div className="bg-white p-4 rounded-xl shadow-sm border border-gray-200">
          <p className="text-sm text-gray-600">Modo Manual</p>
          <p className="text-2xl font-bold text-orange-600">
            {conversations.filter(c => c.mode === 'manual').length}
          </p>
        </div>
        <div className="bg-white p-4 rounded-xl shadow-sm border border-gray-200">
          <p className="text-sm text-gray-600">Automáticas</p>
          <p className="text-2xl font-bold text-blue-600">
            {conversations.filter(c => c.mode === 'automatic').length}
          </p>
        </div>
      </div>

      {/* Chat Interface */}
      <div className="bg-white rounded-xl shadow-sm border border-gray-200 overflow-hidden" style={{height: '600px'}}>
        <div className="flex h-full">
          {/* Conversations List */}
          <div className="w-80 border-r border-gray-200 flex flex-col">
            <div className="p-4 border-b border-gray-200">
              <div className="relative">
                <Search className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-gray-400" />
                <input
                  type="text"
                  value={searchTerm}
                  onChange={(e) => setSearchTerm(e.target.value)}
                  placeholder="Buscar..."
                  className="w-full pl-10 pr-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:border-transparent text-sm"
                  style={{'--tw-ring-color': '#D4AF37'}}
                  onFocus={(e) => e.target.style.boxShadow = '0 0 0 2px rgba(212, 175, 55, 0.5)'}
                  onBlur={(e) => e.target.style.boxShadow = 'none'}
                />
              </div>
            </div>
            
            <div className="flex-1 overflow-y-auto">
              {filteredConversations.length > 0 ? (
                filteredConversations.map((conv) => (
                  <button
                    key={conv.id}
                    onClick={() => setSelectedConv(conv)}
                    className={`w-full p-4 border-b border-gray-100 hover:bg-gray-50 transition-colors text-left ${
                      selectedConv?.id === conv.id ? 'bg-gray-50' : ''
                    }`}
                  >
                    <div className="flex items-start justify-between mb-2">
                      <div className="flex items-center gap-2">
                        <div className="w-10 h-10 rounded-full flex items-center justify-center text-white font-bold text-sm"
                             style={{background: 'linear-gradient(135deg, #D4AF37 0%, #C9A961 100%)'}}>
                          {conv.customer_name.charAt(0).toUpperCase()}
                        </div>
                        <div>
                          <p className="font-semibold text-sm text-gray-800">{conv.customer_name}</p>
                          <div className="flex items-center gap-1 text-xs text-gray-500">
                            <Phone className="h-3 w-3" />
                            {conv.customer_phone}
                          </div>
                        </div>
                      </div>
                      {conv.mode === 'manual' ? (
                        <PowerOff className="h-4 w-4 text-orange-500" title="Modo Manual" />
                      ) : (
                        <Power className="h-4 w-4 text-green-500" title="Modo Automático" />
                      )}
                    </div>
                    <p className="text-xs text-gray-500 line-clamp-2">
                      {conv.messages[conv.messages.length - 1]?.content || 'Sin mensajes'}
                    </p>
                    <div className="flex items-center gap-1 text-xs text-gray-400 mt-1">
                      <Clock className="h-3 w-3" />
                      {new Date(conv.last_message_at).toLocaleString('es-ES', { 
                        day: '2-digit', 
                        month: '2-digit', 
                        hour: '2-digit', 
                        minute: '2-digit' 
                      })}
                    </div>
                  </button>
                ))
              ) : (
                <div className="p-8 text-center text-gray-500">
                  <MessageSquare className="h-12 w-12 mx-auto mb-3 text-gray-400" />
                  <p>No hay conversaciones</p>
                </div>
              )}
            </div>
          </div>

          {/* Chat Area */}
          {selectedConv ? (
            <div className="flex-1 flex flex-col">
              {/* Chat Header */}
              <div className="p-4 border-b border-gray-200 bg-gray-50">
                <div className="flex items-center justify-between">
                  <div>
                    <h3 className="font-bold text-gray-800">{selectedConv.customer_name}</h3>
                    <p className="text-sm text-gray-600">{selectedConv.customer_phone}</p>
                  </div>
                  <button
                    onClick={() => toggleMode(selectedConv.id, selectedConv.mode)}
                    className={`px-4 py-2 rounded-lg font-semibold text-sm transition-all ${
                      selectedConv.mode === 'manual'
                        ? 'bg-orange-100 text-orange-700 hover:bg-orange-200'
                        : 'bg-green-100 text-green-700 hover:bg-green-200'
                    }`}
                  >
                    {selectedConv.mode === 'manual' ? '🔴 Modo Manual' : '🤖 Modo Automático'}
                  </button>
                </div>
              </div>

              {/* Messages */}
              <div className="flex-1 overflow-y-auto p-4 space-y-4">
                {selectedConv.messages.map((msg, idx) => (
                  <motion.div
                    key={idx}
                    initial={{ opacity: 0, y: 10 }}
                    animate={{ opacity: 1, y: 0 }}
                    transition={{ delay: idx * 0.05 }}
                    className={`flex items-start gap-3 ${
                      msg.sender === 'customer' ? '' : 'flex-row-reverse'
                    }`}
                  >
                    <div className={`w-8 h-8 rounded-full flex items-center justify-center flex-shrink-0 ${
                      msg.sender === 'customer' 
                        ? 'bg-gray-200 text-gray-700' 
                        : msg.sender === 'ai'
                        ? 'bg-blue-100 text-blue-700'
                        : 'bg-orange-100 text-orange-700'
                    }`}>
                      {msg.sender === 'customer' ? <User className="h-4 w-4" /> : 
                       msg.sender === 'ai' ? <Bot className="h-4 w-4" /> :
                       <User className="h-4 w-4" />}
                    </div>
                    <div className={`max-w-md ${
                      msg.sender === 'customer' ? 'bg-gray-100' : 
                      msg.sender === 'ai' ? 'bg-blue-50' : 'bg-orange-50'
                    } rounded-2xl px-4 py-3`}>
                      <p className="text-sm text-gray-800">{msg.content}</p>
                      <p className="text-xs text-gray-500 mt-1">
                        {new Date(msg.timestamp).toLocaleTimeString('es-ES', { 
                          hour: '2-digit', 
                          minute: '2-digit' 
                        })}
                      </p>
                    </div>
                  </motion.div>
                ))}
              </div>

              {/* Input */}
              <div className="p-4 border-t border-gray-200 bg-gray-50">
                <div className="flex gap-3">
                  <input
                    type="text"
                    value={messageInput}
                    onChange={(e) => setMessageInput(e.target.value)}
                    onKeyPress={(e) => e.key === 'Enter' && sendMessage()}
                    placeholder="Escribe un mensaje..."
                    className="flex-1 px-4 py-3 border border-gray-300 rounded-lg focus:ring-2 focus:border-transparent"
                    style={{'--tw-ring-color': '#D4AF37'}}
                    onFocus={(e) => e.target.style.boxShadow = '0 0 0 2px rgba(212, 175, 55, 0.5)'}
                    onBlur={(e) => e.target.style.boxShadow = 'none'}
                    disabled={selectedConv.mode === 'automatic'}
                  />
                  <button
                    onClick={sendMessage}
                    disabled={selectedConv.mode === 'automatic' || !messageInput.trim()}
                    className="px-6 py-3 text-white rounded-lg font-semibold transition-all disabled:opacity-50 disabled:cursor-not-allowed"
                    style={{background: 'linear-gradient(135deg, #D4AF37 0%, #C9A961 100%)'}}
                  >
                    <Send className="h-5 w-5" />
                  </button>
                </div>
                {selectedConv.mode === 'automatic' && (
                  <p className="text-xs text-gray-500 mt-2">
                    💡 Cambia a modo manual para enviar mensajes
                  </p>
                )}
              </div>
            </div>
          ) : (
            <div className="flex-1 flex items-center justify-center text-gray-400">
              <div className="text-center">
                <MessageSquare className="h-16 w-16 mx-auto mb-4" />
                <p>Selecciona una conversación</p>
              </div>
            </div>
          )}
        </div>
      </div>
    </div>
  );
};

export default Conversations;
